import type { FullPageErrorModalProps } from "@v2/types/modalTypes";

import * as React from "react";

import twMerge from "@v1/components/common/designSystem/utils/twMerge";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import { Modal } from "@v1/components/designSystem/modal/Modal";
import { ConnectShopifyIntegrationCta } from "@v2/components/ConnectShopifyCallout";
import { useCurrentProjectContext } from "@v2/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import { useModal } from "@v2/store/modalStore";
import { docs } from "@v2/utils/docs";
import { SHOPIFY_APP_LISTING_URL } from "@v2/utils/env";
import { routes } from "@v2/utils/router";
import isArray from "lodash-es/isArray";
import isString from "lodash-es/isString";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { exhaustiveSwitch, isNotNullish } from "replo-utils/lib/misc";

export const FullPageErrorModal: React.FC<FullPageErrorModalProps> = ({
  isClosable,
  details,
}) => {
  const { projectId } = useParams();
  const { project: currentProject } = useCurrentProjectContext();
  const workspaceId = useCurrentWorkspaceId();
  const fullPageError = details;
  const navigate = useNavigate();
  const { closeModal } = useModal();

  if (!fullPageError) {
    return null;
  }

  const {
    header,
    subheader,
    message,
    messageDetail,
    isRecoverable,
    callToAction,
  } = fullPageError;

  const customCallbackData = (() => {
    if (!callToAction) {
      return null;
    }
    if (callToAction.type === "custom" && callToAction.callToActionType) {
      return exhaustiveSwitch(callToAction?.callToActionType)({
        navigateToLink: (data) => {
          return exhaustiveSwitch(data.link)({
            "errors.wrongJsonExtension": () => {
              return {
                name: "How To Fix",
                callback: () => {
                  navigate(docs.errors.wrongJsonExtension);
                },
              };
            },
            "errors.chunkingError": () => {
              return {
                name: "Learn more",
                callback: () => {
                  window.open(docs.errors.chunkingError, "_blank");
                },
              };
            },
            "errors.indexBackupError": () => {
              return {
                name: "Learn more",
                callback: () => {
                  window.open(docs.errors.indexBackupError, "_blank");
                },
              };
            },
            "errors.shopifyIntegrationAlreadyAssigned": () => {
              return {
                name: "Learn more",
                callback: () => {
                  window.open(
                    docs.errors.shopifyIntegrationAlreadyAssigned,
                    "_blank",
                  );
                },
              };
            },
            "errors.shopifyIntegrationMissingAccessScopes": (data) => {
              return {
                name: "Authorize",
                callback: () => {
                  window.open(data.url, "_blank");
                },
              };
            },
            "errors.themeAssetSizeLimitExceeded": () => {
              return {
                name: "Learn more",
                callback: () => {
                  window.open(
                    docs.errors.themeAssetSizeLimitExceeded,
                    "_blank",
                  );
                },
              };
            },
            "errors.themeTotalSizeLimitExceeded": () => {
              return {
                name: "How to Fix",
                callback: () => {
                  window.open(
                    docs.errors.themeTotalSizeLimitExceeded,
                    "_blank",
                  );
                },
              };
            },
            "errors.pageMetafieldDefinitionsChanged": () => {
              return {
                name: "Learn more",
                callback: () => {
                  window.open(
                    docs.errors.pageMetafieldDefinitionsChanged,
                    "_blank",
                  );
                },
              };
            },
            "errors.projectConnectedToBillingIntegrationForShopifyWorkspace":
              () => {
                return {
                  name: "Learn more",
                  callback: () => {
                    window.open(
                      docs.errors
                        .projectConnectedToBillingIntegrationForShopifyWorkspace,
                      "_blank",
                    );
                  },
                };
              },
            "settings.billing": () => {
              return {
                name: "Upgrade Plan",
                callback: () => {
                  navigate(`/workspace/${workspaceId}/settings/billing`);
                },
              };
            },
            customLink: (data) => {
              return {
                name: "View More",
                callback: () => {
                  navigate(data.url);
                },
              };
            },
          });
        },
        navigateToProjectEditor: () => ({
          name: "Go back to project",
          callback: () => {
            closeModal({ type: "fullPageErrorModal" });
            navigate(`/editor/${projectId}`);
          },
        }),
        reload: () => null,
        installApp: () => null,
        goToHomepage: () => null,
        closeModal: () => {
          return {
            name: "Close",
            callback: () => {
              closeModal({ type: "fullPageErrorModal" });
            },
          };
        },
      });
    }
    return null;
  })();

  const button = callToAction ? (
    exhaustiveSwitch(callToAction)({
      link: ({ to, name, target }) => (
        <DeprecatedButton
          variant="primary"
          size="base"
          to={to}
          target={target}
          layoutClassName="w-full"
        >
          {name}
        </DeprecatedButton>
      ),
      callback: ({ onClick, name }) => (
        <DeprecatedButton
          variant="primary"
          size="base"
          layoutClassName="w-full"
          onClick={onClick}
        >
          {name}
        </DeprecatedButton>
      ),
      closeModal: ({ name }) => (
        <DeprecatedButton
          variant="primary"
          size="base"
          layoutClassName="w-full"
          onClick={() => closeModal({ type: "fullPageErrorModal" })}
        >
          {name}
        </DeprecatedButton>
      ),
      // Note (Noah, 2026-01-06): This should never happen, but in the case where
      // there is for some reason no project/workspace, we just redirect to the app
      // store without a cookie. If this ever happens, user will be able to choose
      // the project to connect to after the install flow
      installApp: () => {
        return currentProject?.id && workspaceId ? (
          <ConnectShopifyIntegrationCta
            cookieValue={{
              type: "noShopifyErrorModal",
              projectId: currentProject.id,
              workspaceId,
            }}
          />
        ) : (
          <DeprecatedButton
            variant="primary"
            size="base"
            layoutClassName="w-full"
            to={SHOPIFY_APP_LISTING_URL}
            target="_blank"
          >
            Install Shopify App
          </DeprecatedButton>
        );
      },
      custom: () => (
        <DeprecatedButton
          variant="primary"
          size="base"
          layoutClassName="w-full"
          onClick={customCallbackData?.callback}
        >
          {customCallbackData?.name}
        </DeprecatedButton>
      ),
      navigate: (data) => {
        if (typeof data.location === "string") {
          return exhaustiveSwitch({ type: data.location })({
            home: () => (
              <DeprecatedButton
                variant="primary"
                size="base"
                layoutClassName="w-full"
                to="/"
                onClick={() => {
                  closeModal({ type: "fullPageErrorModal" });
                }}
              >
                Go Home
              </DeprecatedButton>
            ),
            integrationHub: () => (
              <DeprecatedButton
                variant="primary"
                size="base"
                to={`/workspace/${currentProject?.ownerWorkspaceId}/integrations`}
                onClick={() => {
                  return closeModal({ type: "fullPageErrorModal" });
                }}
                layoutClassName="w-full"
              >
                View Integrations
              </DeprecatedButton>
            ),
          });
        }
        return exhaustiveSwitch(data.location)({
          workspace: ({ workspaceId, workspaceName }) => (
            <DeprecatedButton
              variant="primary"
              size="base"
              layoutClassName="w-full"
              to={`/workspace/${workspaceId}`}
              onClick={() => {
                closeModal({ type: "fullPageErrorModal" });
              }}
            >
              Go to "{workspaceName}"
            </DeprecatedButton>
          ),
          home: () => (
            <DeprecatedButton
              variant="primary"
              size="base"
              layoutClassName="w-full"
              to="/"
            >
              Go Home
            </DeprecatedButton>
          ),
          integrationHub: () => (
            <DeprecatedButton
              variant="primary"
              size="base"
              layoutClassName="w-full"
              to={generatePath(routes.shops.settings.shops.integrations.home, {
                projectId: currentProject?.id ?? "",
              })}
            >
              View Integrations
            </DeprecatedButton>
          ),
        });
      },
    })
  ) : (
    <DeprecatedButton
      variant="primary"
      size="base"
      onClick={() => {
        window.location.reload();
      }}
    >
      Refresh Editor
    </DeprecatedButton>
  );

  let messages: React.ReactNode[] | null = null;
  if (isArray(message)) {
    messages = message;
  } else if (message) {
    messages = [message];
  }

  return (
    <Modal
      onOpenChange={(open) => {
        if (!open) {
          closeModal({ type: "fullPageErrorModal" });
        }
      }}
      isOpen
      size="sm"
      title={header}
      description={subheader}
      footer={button}
      isClosable={isClosable}
    >
      {isNotNullish(messages) && (
        <div className="flex w-full flex-col gap-4">
          {messages.map((msg, i) => (
            <p
              key={isString(msg) ? msg : i}
              className={twMerge("text-xs", !isRecoverable && "text-rose")}
            >
              {msg}
            </p>
          ))}
          {messageDetail && (
            <p className="text-muted text-xs">{messageDetail}</p>
          )}
        </div>
      )}
    </Modal>
  );
};
